import { Input, Table } from "reactstrap";
import "./CHMTableComponents.css";
import { handleUpdatePageSlice } from "../redux/slices/globalSlice";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { ICV_STATUS } from "../utils/Constant";
import { Tooltip } from "@mui/material";
import { handleSelectICVActivitySlice, handleBulkSelectICVActivitySlice } from "../redux/slices/icvActivitySlice";
import { LoggedUserType } from "../models/GlobalModels";
import { loginSelector } from "../redux/slices/loginSlice";
import { ROLE_IDS } from "../utils/RBAC";

const ICVActivityTable = ({ viewForm, ICVDashboardData, allowedStatusId }: any) => {
  const dispatch: any = useDispatch();
  const user: LoggedUserType = useSelector(loginSelector).user;
  const { selectedICVActivities } = useSelector((state: any) => state.icvActivity);

  const getEachCHMInfo = (each: any) => {
    viewForm(each);
    dispatch(handleUpdatePageSlice(true));
  };

  const handleSelectItem = (itemId: string, checked: boolean, statusId: any, record: any) => {
    dispatch(handleSelectICVActivitySlice({ itemId, checked, statusId, record }));
  };

  const handleSelectAll = (checked: boolean) => {
    const allowedItems = ICVDashboardData?.filter((item: any) => item?.icvStatusId === allowedStatusId) || [];
    const allowedIds = allowedItems.map((item: any) => item?.icvId);
    
    if (allowedIds.length > 0) {
      dispatch(handleBulkSelectICVActivitySlice({ itemIds: allowedIds, checked, records: allowedItems }));
    }
  };

  const isItemSelected = (itemId: string) => {
    return selectedICVActivities.some((item: any) => item.icvId === itemId);
  };

  const checkSelectAll = () => {
    if (!ICVDashboardData || ICVDashboardData.length === 0) {
      return false;
    }
    else{      
      let allowedData = ICVDashboardData?.filter((item: any) => {
        return item?.icvStatusId === allowedStatusId;
      })      

      if (allowedData.length === 0) {
        return false;
      }

      return allowedData.every((item: any) => 
        selectedICVActivities.some((selectedItem: any) => selectedItem.icvId === item?.icvId)
      );
    }
  }

  const areAllRecordsSelectable = () => {
    return ICVDashboardData?.every((item: any) => item?.icvStatusId !== allowedStatusId);
  };

  const getSelectAllTooltip = () => {
    if (user?.permissions?.includes('Assign-And-Reassign-QC')) {
      if (allowedStatusId === ICV_STATUS.QC_IN_PROGRESS) {
        return "No records with 'QC In Progress' status available for selection";
      }
      else if (allowedStatusId === ICV_STATUS.QC_PENDING) {
        return "No records with 'QC Pending' status available for selection";
      }
    }
    if (user?.permissions?.includes('Bulk-Update')) {
      if (allowedStatusId === ICV_STATUS.ON_HOLD) {
        return "Only records with 'QC On Hold' status can be selected";
      }
    }
  }


  const getSelectIndividualTooltip = () => {
    if (user?.permissions?.includes('Assign-And-Reassign-QC')) {
      if (allowedStatusId === ICV_STATUS.QC_IN_PROGRESS) {
        return "Only records with 'QC In Progress' status can be selected";
      }
      else if (allowedStatusId === ICV_STATUS.QC_PENDING) {
        return "Only records with 'QC Pending' status can be selected";
      }
    }
    if (user?.permissions?.includes('Bulk-Update')) {
      if (allowedStatusId === ICV_STATUS.ON_HOLD) {
        return "Only records with 'QC On Hold' status can be selected";
      }
    }
  }

  const getHelperText = () => {
    if (user?.permissions?.includes('Assign-And-Reassign-QC')) {
      if (allowedStatusId === ICV_STATUS.QC_PENDING) {
        return {
          status: 'QC Pending',
          action: 'QC assignment'
        }
      }
      else if (allowedStatusId === ICV_STATUS.QC_IN_PROGRESS) {
        return {
          status: 'QC In Progress',
          action: 'QC reassignment'
        }
      }
    }
    if (user?.permissions?.includes('Bulk-Update')) {
      if (allowedStatusId === ICV_STATUS.ON_HOLD) {
        return {
          status: 'QC On Hold',
          action: 'Bulk Update'
        }
      }
    }
  }

  return (
    <div className="table-scroll-container">
      {(user?.permissions?.includes('Bulk-Update') || user?.permissions?.includes('Assign-And-Reassign-QC')) && 
        (getHelperText() && <p className="helper-text">Only {getHelperText()?.status} surveys can be selected for {getHelperText()?.action}.</p>)
      }
      <Table striped hover bordered responsive className="icv-table">
        <thead>
          <tr className="aligne-center">
            {(
              (user?.permissions?.includes('Bulk-Update') && allowedStatusId === ICV_STATUS.ON_HOLD) || 
              user?.permissions?.includes('Assign-And-Reassign-QC')) &&
              <th className="sticky-left">
                {areAllRecordsSelectable() ? (
                  <Tooltip
                    title={getSelectAllTooltip()}
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: 'black',
                          fontSize: '12px'
                        }
                      }
                    }}
                  >
                    <span>
                      <Input 
                        type="checkbox"
                        className='custom-checkbox-table'
                        checked={checkSelectAll()}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        disabled={areAllRecordsSelectable()}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Input 
                    type="checkbox"
                    className='custom-checkbox-table'
                    checked={checkSelectAll()}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={areAllRecordsSelectable()}
                  />
                )}
              </th>
            }
            <th className="sticky-left">KML-ID</th>
            <th>State</th>
            <th>District</th>
            <th>Block</th>
            <th>Village</th>
            <th>Survey Number</th>
            <th>Surveyor Name</th>
            <th>Surveyor Mobile Number</th>
            <th>Survey Initiation Date</th>
            <th>Survey Completion Date</th>
            <th>Survey Remarks</th>
            <th>QC Executive Name</th>
            <th>QC Initiated Date</th>
            <th>QC Completion Date</th>
            <th>QC Remarks</th>
            <th className="sticky-right">Status</th>
          </tr>
        </thead>
        <tbody>
          {ICVDashboardData?.map((each: any, index: any) => {
            return (
              <tr 
                key={`ICVTable-${index}`} 
                className={`${each?.icvStatusId === ICV_STATUS.REASSIGNED ? 'reassigned-row' : 'aligne-center'}`}
              >
                {((user?.permissions?.includes('Bulk-Update') && allowedStatusId === ICV_STATUS.ON_HOLD) || user?.permissions?.includes('Assign-And-Reassign-QC')) &&
                  <td
                    style={{position:'sticky', left:'0'}}
                  >
                    {each?.icvStatusId !== allowedStatusId ? (
                      <Tooltip
                        title={getSelectIndividualTooltip()}
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: 'black',
                              fontSize: '12px'
                            }
                          }
                        }}
                      >
                        <span>
                          <Input 
                            type="checkbox"
                            className='custom-checkbox-table'
                            checked={isItemSelected(each.icvId) && each?.icvStatusId === allowedStatusId}
                            disabled={each?.icvStatusId !== allowedStatusId}
                            onChange={(e) => handleSelectItem(each.icvId, e.target.checked, each.icvStatusId, each)}
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <Input 
                        type="checkbox"
                        className='custom-checkbox-table'
                        checked={isItemSelected(each.icvId) && each?.icvStatusId === allowedStatusId}
                        disabled={each?.icvStatusId !== allowedStatusId}
                        onChange={(e) => handleSelectItem(each.icvId, e.target.checked, each.icvStatusId, each)}
                      />
                    )}
                  </td>
                }
                <td
                  className="view-form"
                  style={{position:'sticky', left:'0'}}
                  onClick={() => {
                    getEachCHMInfo(each);
                  }}
                >{each.kmlId}</td>
                <td>{each.state ?? '--'}</td>
                <td>{each.district ?? '--'}</td>
                <td>{each.mandal ?? '--'}</td>
                <td>{each.village ?? '--'}</td>
                <td>{each.surveyNumber ?? '--'}</td>
                <td>{each.surveyorName ?? '--'}</td>
                <td>{each.surveyorMobileNumber ?? '--'}</td>
                <td>{each.surveyInitiationDate?.split("T")[0]?.split("-")?.reverse()?.join("-") ?? '--'}</td>
                <td>{each.surveyCompletionDate?.split("T")[0]?.split("-")?.reverse()?.join("-") ?? '--'}</td>
                <td className="remarks-field">
                  <Tooltip
                    title={each.surveyRemarks}
                    placement="bottom-start"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: 'black'
                        }
                      }
                    }}
                  >
                    <span>{each.surveyRemarks?.length > 0 ? each.surveyRemarks : '--'}</span>
                  </Tooltip>
                </td>
                <td>{each.qcName ?? '--'}</td>
                <td>{each.qcInitiatedDate?.split("T")[0]?.split("-")?.reverse()?.join("-") ?? '--'}</td>
                <td>{each?.icvStatusId === ICV_STATUS.COMPLETED ? (each.qcCompletionDate?.split("T")[0]?.split("-")?.reverse()?.join("-") ?? '--') : '--'}</td>
                <td className="remarks-field">
                  <Tooltip
                    title={each.additionalRemarksByQc}
                    placement="bottom-start"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: 'black'
                        }
                      }
                    }}
                  ><span>{each.additionalRemarksByQc?.trim()?.length > 0 ? each.additionalRemarksByQc?.trim() : '--'}</span>
                  </Tooltip>
                </td>
                <td className="sticky-right">
                  <span id={`status-${each?.icvStatusId}`} className="status-cell">{each.icvStatusDescription ?? '--'}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default ICVActivityTable;
